/**
 * Search a work order by number or plate and assign an inbound document (PF-136).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useDebounce } from '@/hooks/useDebounce';
import {
  MIN_SEARCH_LENGTH,
  useGlobalSearch,
  type GlobalSearchResult,
} from '@/hooks/useGlobalSearch';
import type { InboundDocument } from '@/hooks/useInboundDocuments';

interface AssignInboundDocumentDialogProps {
  open: boolean;
  document: InboundDocument | null;
  loading: boolean;
  onClose: () => void;
  onAssign: (bookingId: string) => void;
}

export function AssignInboundDocumentDialog({
  open,
  document,
  loading,
  onClose,
  onAssign,
}: AssignInboundDocumentDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<GlobalSearchResult | null>(null);
  const debouncedQuery = useDebounce(query, 300);
  const search = useGlobalSearch(debouncedQuery, 8);

  const options = useMemo(() => {
    if (debouncedQuery.trim().length < MIN_SEARCH_LENGTH) return [];
    return (search.data?.results ?? []).filter((result) => result.type === 'work_order');
  }, [debouncedQuery, search.data?.results]);

  const handleClose = () => {
    setQuery('');
    setSelected(null);
    onClose();
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      mobileTitle={t('inbox.assign')}
    >
      <DialogTitle>{t('inbox.assign')}</DialogTitle>
      <DialogContent>
        <Autocomplete
          sx={{ mt: 1 }}
          options={options}
          value={selected}
          inputValue={query}
          loading={search.isFetching}
          filterOptions={(opts) => opts}
          getOptionLabel={(option) =>
            option.subtitle
              ? t('inbox.searchOption', { title: option.title, subtitle: option.subtitle })
              : option.title
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onInputChange={(_event, value) => {
            setQuery(value);
          }}
          onChange={(_event, value) => {
            setSelected(value);
          }}
          noOptionsText={tc('noResults')}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label={t('inbox.searchWorkOrder')}
              helperText={t('inbox.searchWorkOrderHint')}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={selected === null || loading || document === null}
          onClick={() => {
            if (selected === null) return;
            onAssign(selected.id);
          }}
        >
          {t('inbox.assignConfirm')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
